// src/pages/POSPage.jsx
import React, { useCallback, useMemo, useEffect, useState } from "react";
import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import SearchBar from "../components/pos/SearchBar";
import ProductGrid from "../components/pos/ProductGrid";
import OrderDetails from "../components/pos/OrderDetails";
import MobileOrderSheet from "../components/pos/MobileOrderSheet";
import SaleSubmitter from "../components/pos/SaleSubmitter";
import OptionPickerModal from "../components/pos/OptionPickerModal";
import LowStockReminderModal, {
  buildLowStockAlerts,
} from "../components/pos/LowStockReminderModal";
import { ShoppingCart, ChevronUp } from "lucide-react";
import toast from "react-hot-toast";

import { getProducts, getProductBySKU } from "../api/products";
import { getCategories, getSubCategories } from "../api/categories";
import { getMe } from "../api/users";
import { listStoreLocations } from "../api/storeLocations";
import { toAbsoluteUrl } from "../api/client";
import { listDiscounts } from "../api/discounts";
import { listAdditionalCharges } from "../api/additionalCharges";
import {
  normalizeOptionGroups,
  sumOptionsPrice,
  buildCartLineKey,
} from "../api/productOptions";
import {
  getCurrentRegister,
  openRegister,
  closeRegister,
  getRegisterSession,
  saveRegisterCart,
} from "../api/registerSessions";
import {
  OpenRegisterModal,
  RegisterSummaryModal,
} from "../components/pos/RegisterModals";
import { canSwitchStores, isHqAdmin } from "../utils/roles";

const PER_PAGE = 30;
const TAX_RATE = 0;

const DEFAULT_CHECKOUT = {
  payment_method: "cash",
  customer_name: "General",
  // Person the order is for. Distinct from customer_name (the type enum).
  buyer_name: "",
  paid: "",
  reference: "",
  note: "",
  global_discount_id: null,
  // Selected member object (customer database). Cleared with the rest of the
  // checkout after each sale so the next customer never inherits it.
  member: null,
};

const normalizeCartItem = (item) => {
  if (!item || item.id == null) return null;
  const inventoryType = String(
    item.inventoryType ?? item.inventory_type ?? "stock"
  ).toLowerCase();

  const selectedOptions = Array.isArray(item.selected_options)
    ? item.selected_options
    : [];
  const optionsPrice = selectedOptions.length
    ? sumOptionsPrice(selectedOptions)
    : Number(item.options_price ?? 0);
  const basePrice = Number(item.base_price ?? item.price ?? 0);

  return {
    ...item,
    id: item.id,
    line_key: item.line_key || buildCartLineKey(item.id, selectedOptions),
    selected_options: selectedOptions,
    options_price: optionsPrice,
    base_price: basePrice,
    price: basePrice + optionsPrice,
    quantity: Math.max(1, Number(item.quantity ?? 1)),
    discount_type: item.discount_type || "%",
    discount_value: Number(item.discount_value ?? 0),
    item_discount_id: item.item_discount_id ?? null,
    inventoryType,
    isStockTracked:
      item.isStockTracked ?? item.is_stock_tracked ?? inventoryType === "stock",
    hasRecipe: !!(item.hasRecipe ?? item.has_recipe),
    availableToMake:
      item.availableToMake != null
        ? Number(item.availableToMake)
        : item.available_to_make != null
        ? Number(item.available_to_make)
        : null,
    recipeBottleneck: item.recipeBottleneck ?? item.recipe_bottleneck ?? null,
  };
};

/* ===== Debounce util ===== */
function useDebouncedValue(value, delay = 300) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

/* ===== Normalizer produk (pakai inventory_type) ===== */
const normalize = (p) => {
  // backend: products.inventory_type (stock | non_stock | service | bundle | dll)
  const inventoryTypeRaw =
    p.inventory_type ??
    p.inventoryType ??
    p.type ??
    "stock"; // default: stock

  const inventoryType = String(inventoryTypeRaw || "stock").toLowerCase();

  // Hanya inventory_type = "stock" yang benar-benar pakai stok fisik
  const isStockTracked = inventoryType === "stock";

  return {
    id: p.id ?? p.product_id ?? p.sku,
    name: p.name ?? p.product_name ?? p.nama ?? "Tanpa Nama",
    price: Number(p.price ?? p.unit_price ?? p.sale_price ?? 0),
    image: toAbsoluteUrl(
      p.image_url || p.image || p.thumbnail_url || p.photo_url || null
    ),
    stock: p.stock ?? p.qty ?? p.quantity ?? 0,
    sku: p.sku ?? p.barcode ?? null,
    category_id: p.category_id,
    sub_category_id: p.sub_category_id,
    inventoryType,     // ⬅️ simpan tipe
    isStockTracked,    // ⬅️ dipakai di FE
    hasRecipe: !!(p.has_recipe ?? p.hasRecipe),
    availableToMake:
      p.available_to_make != null
        ? Number(p.available_to_make)
        : p.availableToMake != null
        ? Number(p.availableToMake)
        : null,
    recipeBottleneck: p.recipe_bottleneck ?? p.recipeBottleneck ?? null,
    optionGroups: normalizeOptionGroups(p),
  };
};

export default function POSPage() {
  /* ===== UI states ===== */
  const [q, setQ] = React.useState("");
  const debouncedQ = useDebouncedValue(q, 300);

  const [filters, setFilters] = React.useState({
    category_id: undefined,
    sub_category_id: undefined,
    stock_status: "any",
  });
  const [pickedCategory, setPickedCategory] = React.useState(undefined);

  const [cartItems, setCartItems] = React.useState([]);
  const [cartCheckout, setCartCheckout] = React.useState(DEFAULT_CHECKOUT);
  const [cartHydrated, setCartHydrated] = React.useState(false);
  const cartSaveTimerRef = React.useRef(null);
  const hydratedSessionIdRef = React.useRef(null);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [optionPickerProduct, setOptionPickerProduct] = React.useState(null);

  /* ===== /api/me (role & store) ===== */
  const meQ = useQuery({
    queryKey: ["me"],
    queryFn: ({ signal }) => getMe(signal),
    retry: 1,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const role = (meQ.data?.role || "").toLowerCase();
  const isHqAdminUser = isHqAdmin(role);
  const showStoreSelector = canSwitchStores(role, meQ.data);
  const myStoreIdFromMe = meQ.isSuccess
    ? meQ.data?.store_location?.id ?? meQ.data?.store_location_id ?? null
    : null;

  /* ===== stores (untuk admin) ===== */
  const storesQ = useQuery({
    queryKey: ["stores", { per_page: 200 }],
    queryFn: ({ signal }) => listStoreLocations({ per_page: 200 }, signal),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: showStoreSelector || meQ.isError,
  });
  const stores = storesQ.data?.items ?? [];

  /* ===== selected store ===== */
  const [selectedStoreId, setSelectedStoreId] = React.useState(undefined);

  // default dari /api/me
  useEffect(() => {
    if (meQ.isSuccess && selectedStoreId === undefined) {
      if (isHqAdminUser) {
        setSelectedStoreId(
          myStoreIdFromMe != null ? String(myStoreIdFromMe) : "ALL"
        );
      } else {
        setSelectedStoreId(
          myStoreIdFromMe != null ? String(myStoreIdFromMe) : ""
        );
      }
    }
  }, [meQ.isSuccess, isHqAdminUser, myStoreIdFromMe, selectedStoreId]);

  // fallback jika /api/me error → admin-like dengan ALL
  useEffect(() => {
    if (meQ.isError && selectedStoreId === undefined) {
      setSelectedStoreId("ALL");
    }
  }, [meQ.isError, selectedStoreId]);

  const discountStoreParam = useMemo(() => {
    if (!selectedStoreId || selectedStoreId === "ALL" || selectedStoreId === "") {
      return undefined;
    }
    const n = Number(selectedStoreId);
    return Number.isFinite(n) ? n : undefined;
  }, [selectedStoreId]);

  /** Branch used for register + checkout (must match product list). */
  const posStoreId = discountStoreParam;

  const posDiscountsQ = useQuery({
    queryKey: ["pos", "discounts", discountStoreParam ?? "ALL"],
    enabled: meQ.isSuccess && selectedStoreId !== undefined && selectedStoreId !== "",
    queryFn: async ({ signal }) => {
      const base = {
        active: 1,
        per_page: 50,
        ...(discountStoreParam != null
          ? { store_location_id: discountStoreParam }
          : {}),
      };
      try {
        const [itemRes, globalRes] = await Promise.all([
          listDiscounts({ ...base, scope: "ITEM" }, signal),
          listDiscounts({ ...base, scope: "GLOBAL" }, signal),
        ]);
        return {
          item: itemRes.items ?? [],
          global: globalRes.items ?? [],
        };
      } catch {
        return { item: [], global: [] };
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const itemDiscounts = posDiscountsQ.data?.item ?? [];
  const globalDiscounts = posDiscountsQ.data?.global ?? [];

  const additionalChargesQ = useQuery({
    queryKey: ["pos", "additional-charges", discountStoreParam ?? "ALL"],
    enabled:
      meQ.isSuccess &&
      selectedStoreId !== undefined &&
      selectedStoreId !== "" &&
      discountStoreParam != null,
    queryFn: async ({ signal }) => {
      try {
        const res = await listAdditionalCharges(
          { store_location_id: discountStoreParam },
          signal
        );
        const payload = res?.data;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload)) return payload;
        return [];
      } catch {
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const additionalCharges = additionalChargesQ.data ?? [];

  const categoryStoreParam = discountStoreParam;

  /* ===== Categories (scoped to selected cabang) ===== */
  const { data: categories = [] } = useQuery({
    queryKey: ["categories", categoryStoreParam ?? "ALL"],
    enabled: meQ.isSuccess && selectedStoreId !== undefined && selectedStoreId !== "",
    queryFn: ({ signal }) =>
      getCategories(
        {
          per_page: 200,
          ...(categoryStoreParam != null
            ? { store_location_id: categoryStoreParam }
            : {}),
        },
        signal
      ),
    staleTime: 30 * 60 * 1000,
  });

  const { data: subCategories = [] } = useQuery({
    queryKey: ["subCategories", pickedCategory || null, categoryStoreParam ?? "ALL"],
    queryFn: ({ signal }) =>
      getSubCategories(pickedCategory, signal, {
        ...(categoryStoreParam != null
          ? { store_location_id: categoryStoreParam }
          : {}),
      }),
    enabled: !!pickedCategory && meQ.isSuccess,
    staleTime: 30 * 60 * 1000,
  });

  // Reset product filter when cabang changes
  useEffect(() => {
    if (selectedStoreId === undefined) return;
    setFilters({
      category_id: undefined,
      sub_category_id: undefined,
      stock_status: "any",
    });
    setPickedCategory(undefined);
  }, [selectedStoreId]);

  const mainScrollRef = React.useRef(null);

  const queryClient = useQueryClient();

  // ===== Register session =====
  const pickCurrentRegister = (raw) => {
    if (!raw) return null;
    const candidate =
      (raw && typeof raw === "object" && !Array.isArray(raw) && raw.session) ||
      raw;
    if (
      candidate &&
      typeof candidate === "object" &&
      !Array.isArray(candidate) &&
      (typeof candidate.id === "number" || typeof candidate.id === "string")
    ) {
      return candidate;
    }
    return null;
  };

  const registerQ = useQuery({
    queryKey: ["register", "current", posStoreId ?? "user-default"],
    enabled: meQ.isSuccess,
    queryFn: async ({ signal }) => {
      try {
        const params =
          posStoreId != null ? { store_location_id: posStoreId } : {};
        return await getCurrentRegister(signal, params);
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // normalisasi bentuk data register
  const currentRegister = pickCurrentRegister(registerQ.data);
  const registerReady = !!currentRegister;

  const saleStoreId = useMemo(() => {
    const fromRegister = currentRegister?.store_location_id;
    if (fromRegister != null && fromRegister !== "") {
      return Number(fromRegister);
    }
    return posStoreId ?? null;
  }, [currentRegister?.store_location_id, posStoreId]);

  const saleExtraPayload = useMemo(
    () =>
      saleStoreId != null ? { store_location_id: saleStoreId } : {},
    [saleStoreId]
  );

  // Keep branch picker aligned with open register session
  useEffect(() => {
    const sid = currentRegister?.store_location_id;
    if (sid == null || sid === "") return;
    setSelectedStoreId((prev) => {
      const next = String(sid);
      return prev === next ? prev : next;
    });
  }, [currentRegister?.store_location_id]);

  const clearCartState = useCallback(() => {
    setCartItems([]);
    setCartCheckout(DEFAULT_CHECKOUT);
  }, []);

  const persistCartToServer = useCallback(
    async (items, checkout) => {
      const sessionId = currentRegister?.id;
      if (!sessionId) return;
      try {
        await saveRegisterCart(sessionId, { items, checkout });
      } catch {
        // silent — cart still works locally; will retry on next change
      }
    },
    [currentRegister?.id]
  );

  // Restore cart once per open register session (from server)
  useEffect(() => {
    if (registerQ.isLoading) return;

    if (!currentRegister?.id) {
      hydratedSessionIdRef.current = null;
      setCartHydrated(false);
      clearCartState();
      return;
    }

    if (hydratedSessionIdRef.current === currentRegister.id) return;
    hydratedSessionIdRef.current = currentRegister.id;

    const saved = currentRegister.cart_data;
    if (saved && Array.isArray(saved.items) && saved.items.length > 0) {
      const restored = saved.items.map(normalizeCartItem).filter(Boolean);
      if (restored.length > 0) {
        setCartItems(restored);
        toast.success(`Cart dipulihkan (${restored.length} item)`);
      } else {
        setCartItems([]);
      }
    } else {
      setCartItems([]);
    }

    if (saved?.checkout && typeof saved.checkout === "object") {
      setCartCheckout({ ...DEFAULT_CHECKOUT, ...saved.checkout });
    } else {
      setCartCheckout(DEFAULT_CHECKOUT);
    }

    setCartHydrated(true);
  }, [currentRegister?.id, currentRegister?.cart_data, registerQ.isLoading, clearCartState]);

  // Auto-save cart to server (debounced)
  useEffect(() => {
    if (!cartHydrated || !currentRegister?.id) return;

    clearTimeout(cartSaveTimerRef.current);
    cartSaveTimerRef.current = setTimeout(() => {
      persistCartToServer(cartItems, cartCheckout);
    }, 600);

    return () => clearTimeout(cartSaveTimerRef.current);
  }, [
    cartItems,
    cartCheckout,
    cartHydrated,
    currentRegister?.id,
    persistCartToServer,
  ]);

  const [openRegisterModal, setOpenRegisterModal] = useState(false);
  const [registerSummary, setRegisterSummary] = useState(null);
  const [registerClosed, setRegisterClosed] = useState(false);

  const openRegisterMutation = useMutation({
    mutationFn: openRegister,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["register", "current"] });
      setOpenRegisterModal(false);
      toast.success("Register berhasil dibuka.");
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Gagal membuka register"
      );
    },
  });

  const closeRegisterMutation = useMutation({
    mutationFn: ({ id, payload }) => closeRegister(id, payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["register", "current"] });
      setRegisterSummary(res);
      setRegisterClosed(true);
      clearCartState();
      setCartHydrated(false);
      toast.success("Register ditutup.");
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Gagal menutup register"
      );
    },
  });

  /* ===== Products (infinite) ===== */
  const productsQuery = useInfiniteQuery({
    queryKey: [
      "products",
      { q: debouncedQ, ...filters, scope: selectedStoreId ?? "NONE" },
    ],
    queryFn: async ({ pageParam = 1, signal }) => {
      const isAll = selectedStoreId === "ALL";
      const params = {
        search: debouncedQ || undefined,
        page: pageParam,
        per_page: PER_PAGE,
        category_id: filters.category_id,
        sub_category_id: filters.sub_category_id,
        in_stock: filters.stock_status === "available" ? 1 : undefined,
        out_of_stock: filters.stock_status === "out" ? 1 : undefined,
        store_id: !isAll && selectedStoreId ? Number(selectedStoreId) : undefined,
        only_store: !isAll && selectedStoreId ? 1 : undefined,
        exclude_recipe_ingredients: !isAll && selectedStoreId ? 1 : undefined,
      };
      return getProducts(params, signal);
    },
    enabled: selectedStoreId !== undefined && (showStoreSelector || !!selectedStoreId),
    getNextPageParam: (lastPage) => {
      const m = lastPage?.meta;
      return m && m.current_page < m.last_page ? m.current_page + 1 : undefined;
    },
    staleTime: 30 * 1000,
    gcTime: 30 * 60 * 1000,
    // Always pull a fresh catalog when opening POS or returning to this tab,
    // so GR / write-off / recipe stock changes show without a hard refresh.
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Quiet background refresh while the cashier stays on POS.
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    keepPreviousData: true,
  });

  const flatProducts = useMemo(() => {
    const pages = productsQuery.data?.pages || [];
    const merged = pages.flatMap((p) => (p.items || []).map(normalize));
    const map = new Map(merged.map((x) => [x.id, x]));
    return Array.from(map.values());
  }, [productsQuery.data]);

  /* ===== Filter stok di FE (aware non-stock + recipe can-make) =====
     available: stock qty>0, OR recipe can-make>0, OR non-stock tanpa resep
     out      : stock qty<=0, OR recipe can-make<=0
  */
  const filteredProducts = useMemo(() => {
    let arr = flatProducts;

    const effectiveQty = (p) => {
      if (p.hasRecipe && p.availableToMake != null) return Number(p.availableToMake);
      if (p.isStockTracked) return Number(p.stock ?? 0);
      return Infinity; // non-stock, no recipe
    };

    if (filters.stock_status === "available") {
      arr = arr.filter((p) => effectiveQty(p) > 0);
    } else if (filters.stock_status === "out") {
      arr = arr.filter((p) => {
        if (p.hasRecipe && p.availableToMake != null) return Number(p.availableToMake) <= 0;
        return p.isStockTracked && Number(p.stock ?? 0) <= 0;
      });
    }

    return arr;
  }, [flatProducts, filters.stock_status]);

  /* ===== Low ingredient / stock reminder ===== */
  const [lowStockOpen, setLowStockOpen] = useState(false);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const dismissedLowStockRef = React.useRef(new Set());
  const lastLowStockStoreRef = React.useRef(null);

  useEffect(() => {
    // Reset dismiss memory when switching cabang.
    if (lastLowStockStoreRef.current !== posStoreId) {
      lastLowStockStoreRef.current = posStoreId;
      dismissedLowStockRef.current = new Set();
      setLowStockOpen(false);
      setLowStockAlerts([]);
    }
  }, [posStoreId]);

  useEffect(() => {
    if (posStoreId == null) return;
    if (productsQuery.isLoading || productsQuery.isFetchingNextPage) return;
    if (!flatProducts.length) return;

    const all = buildLowStockAlerts(flatProducts);
    if (!all.length) {
      setLowStockOpen(false);
      setLowStockAlerts([]);
      return;
    }

    const fresh = all.filter(
      (a) => !dismissedLowStockRef.current.has(`${a.id}:${a.critical ? "out" : "low"}`)
    );
    if (!fresh.length) return;

    setLowStockAlerts(fresh);
    setLowStockOpen(true);
  }, [
    flatProducts,
    posStoreId,
    productsQuery.isLoading,
    productsQuery.isFetchingNextPage,
    productsQuery.dataUpdatedAt,
  ]);

  const dismissLowStockReminder = useCallback(() => {
    for (const a of lowStockAlerts) {
      dismissedLowStockRef.current.add(`${a.id}:${a.critical ? "out" : "low"}`);
    }
    setLowStockOpen(false);
  }, [lowStockAlerts]);

  const hasMore = !!productsQuery.hasNextPage;
  const loading =
    (productsQuery.isFetching && !productsQuery.isFetchingNextPage) ||
    meQ.isLoading ||
    registerQ.isLoading ||
    (isHqAdminUser && storesQ.isLoading && selectedStoreId === undefined);
  const loadingMore = productsQuery.isFetchingNextPage;
  const err =
    productsQuery.isError
      ? "Gagal memuat produk"
      : storesQ.isError && showStoreSelector
      ? "Gagal memuat daftar cabang"
      : meQ.isError
      ? "Gagal memuat data user"
      : "";

  /* ===== Cart handlers ===== */
  const maxSellableQty = useCallback((product) => {
    if (product?.hasRecipe && product.availableToMake != null) {
      return Math.max(0, Number(product.availableToMake));
    }
    if (product?.isStockTracked) {
      return Math.max(0, Number(product.stock ?? 0));
    }
    return Infinity;
  }, []);

  /**
   * Tambah ke cart. `selectedOptions` = [{group_id, group, value_id, name, price_delta}]
   * Produk sama dengan opsi berbeda = baris terpisah (line_key).
   */
  const handleAddToCart = useCallback(
    (product, selectedOptions = []) => {
      const maxQty = maxSellableQty(product);
      if (maxQty <= 0) {
        toast.error(
          product.recipeBottleneck
            ? `Tidak bisa dibuat — stok ${product.recipeBottleneck} habis`
            : "Stok tidak cukup"
        );
        return;
      }

      const options = selectedOptions || [];
      const lineKey = buildCartLineKey(product.id, options);
      const optionsPrice = sumOptionsPrice(options);

      setCartItems((prev) => {
        // stok dihitung dari total qty semua baris produk yang sama
        const usedQty = prev
          .filter((i) => i.id === product.id)
          .reduce((acc, i) => acc + Number(i.quantity || 0), 0);

        if (usedQty + 1 > maxQty) {
          toast.error(
            product.hasRecipe
              ? `Maksimal bisa dibuat ${maxQty} (bahan terbatas)`
              : `Stok hanya ${maxQty}`
          );
          return prev;
        }

        const exist = prev.find((i) => i.line_key === lineKey);
        if (exist) {
          return prev.map((i) =>
            i.line_key === lineKey ? { ...i, quantity: i.quantity + 1 } : i
          );
        }

        return [
          ...prev,
          {
            ...product,
            line_key: lineKey,
            quantity: 1,
            discount_type: "%",
            discount_value: 0,
            selected_options: options,
            options_price: optionsPrice,
            // harga dasar disimpan supaya bisa dihitung ulang
            base_price: Number(product.price || 0),
            price: Number(product.price || 0) + optionsPrice,
          },
        ];
      });
    },
    [maxSellableQty]
  );

  const handleUpdateQuantity = useCallback(
    (lineKey, change) => {
      setCartItems((prev) => {
        const target = prev.find((i) => i.line_key === lineKey);
        if (!target) return prev;

        const next = target.quantity + change;
        if (next <= 0) return prev.filter((i) => i.line_key !== lineKey);

        const maxQty = maxSellableQty(target);
        const usedByOthers = prev
          .filter((i) => i.id === target.id && i.line_key !== lineKey)
          .reduce((acc, i) => acc + Number(i.quantity || 0), 0);

        if (usedByOthers + next > maxQty) {
          toast.error(
            target.hasRecipe
              ? `Maksimal bisa dibuat ${maxQty}`
              : `Stok hanya ${maxQty}`
          );
          return prev;
        }

        return prev.map((i) =>
          i.line_key === lineKey ? { ...i, quantity: next } : i
        );
      });
    },
    [maxSellableQty]
  );

  const handleUpdateDiscount = useCallback((lineKey, payload) => {
    setCartItems((prev) =>
      prev.map((it) =>
        it.line_key === lineKey ? { ...it, ...payload } : it
      )
    );
  }, []);

  const handleRemoveItem = useCallback((lineKey) => {
    setCartItems((prev) => prev.filter((i) => i.line_key !== lineKey));
  }, []);

  /**
   * Klik produk: kalau punya grup opsi → buka modal dulu.
   * Kalau tidak → langsung masuk cart (perilaku lama).
   */
  const handleProductClick = useCallback(
    (product) => {
      if (product?.optionGroups?.length) {
        setOptionPickerProduct(product);
        return;
      }
      handleAddToCart(product, []);
    },
    [handleAddToCart]
  );

  const handleConfirmOptions = useCallback(
    (selected) => {
      if (optionPickerProduct) {
        handleAddToCart(optionPickerProduct, selected);
      }
      setOptionPickerProduct(null);
    },
    [optionPickerProduct, handleAddToCart]
  );

  const handleClearCart = useCallback(() => {
    clearCartState();
    if (currentRegister?.id) {
      persistCartToServer([], DEFAULT_CHECKOUT);
    }
  }, [clearCartState, currentRegister?.id, persistCartToServer]);

  /* ===== Search / Filter / Category pick / Scan ===== */
  const handleSearch = useCallback((text) => setQ(text), []);
  const handleFilterChange = useCallback((f) => setFilters(f), []);
  const handlePickCategory = useCallback(
    (catId) => setPickedCategory(catId || undefined),
    []
  );

  const handleScan = useCallback(
    async (code) => {
      try {
        const cleanCode = code.trim().toUpperCase();
        const isAll = selectedStoreId === "ALL";

        const p = await getProductBySKU(
          cleanCode,
          isAll ? {} : { store_id: Number(selectedStoreId), only_store: 1 }
        );

        if (!p) return toast.error(`Kode ${cleanCode} tidak ditemukan`);
        if (p.sku?.toUpperCase() !== cleanCode) {
          console.warn(`SKU mismatch: expected ${cleanCode}, got ${p.sku}`);
          return toast.error(`Produk tidak ditemukan`);
        }
        // pakai normalize supaya inventoryType & isStockTracked ikut.
        // handleProductClick → produk beropsi tetap buka modal pilihan dulu.
        const normalized = normalize(p);
        handleProductClick(normalized);
        if (!normalized.optionGroups?.length) {
          toast.success(`${p.name} ditambahkan ke cart`);
        }
      } catch (e) {
        console.error(e);
        toast.error("Scanner error. Coba lagi.");
      }
    },
    [handleProductClick, selectedStoreId]
  );

  /* ===== Totals ===== */
  const subtotalItems = useMemo(() => {
    return cartItems.reduce((s, i) => {
      const price = Number(i.price || 0);
      const qty = Number(i.quantity || 0);
      const type = i.discount_type || "%";
      const val = Number(i.discount_value || 0);

      const discNominal = Math.min(
        price,
        type === "%" ? (price * val) / 100 : val
      );

      const netUnit = Math.max(0, price - discNominal);
      return s + netUnit * qty;
    }, 0);
  }, [cartItems]);
  const tax = useMemo(
    () => Math.round(subtotalItems * TAX_RATE),
    [subtotalItems]
  );
  const total = subtotalItems + tax;

  /* ===== Infinite scroll trigger ===== */
  useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return;

    function onScroll() {
      if (!hasMore || productsQuery.isFetchingNextPage) return;

      const { scrollTop, scrollHeight, clientHeight } = el;

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        productsQuery.fetchNextPage();
      }
    }

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [hasMore, productsQuery.isFetchingNextPage]);

  return (
    <div className="flex flex-col md:flex-row h-screen bg-gray-50">
      {/* Main Content */}
      <main className="order-1 flex-1 flex flex-col relative bg-gray-50">

        {/* ===== SearchBar (Sticky Header) ===== */}
        <div className="sticky top-0 z-30 bg-gray-50">
          <div className="max-w-6xl mx-auto px-3 sm:px-5 md:px-6 py-2 sm:py-3">
            <SearchBar
              key={selectedStoreId ?? "none"}
              onSearch={handleSearch}
              onScan={handleScan}
              onFilterChange={handleFilterChange}
              categories={categories}
              subCategories={subCategories}
              onPickCategory={setPickedCategory}
              showStoreSelector={showStoreSelector}
              storeOptions={[
                { value: "ALL", label: "Semua" },
                ...stores.map((s) => ({
                  value: String(s.id),
                  label: s.name,
                })),
              ]}
              selectedStoreId={selectedStoreId || "ALL"}
              onChangeStore={(val) => {
                if (currentRegister) {
                  toast.error("Tutup register dulu sebelum ganti cabang.");
                  return;
                }
                setSelectedStoreId(val || "ALL");
              }}
              storeDisabled={storesQ.isLoading || !!currentRegister}
              registerState={
                currentRegister
                  ? { status: "open", session: currentRegister }
                  : { status: "closed" }
              }
              onClickRegister={() => {
                if (currentRegister) {
                  // buka modal summary (preview) tanpa langsung close
                  getRegisterSession(currentRegister.id)
                    .then((res) => {
                      setRegisterSummary(res);
                      setRegisterClosed(false);
                    })
                    .catch((err) => {
                      toast.error(
                        err?.response?.data?.message ||
                          err?.message ||
                          "Gagal memuat summary register"
                      );
                    });
                } else {
                  if (posStoreId == null) {
                    toast.error("Pilih cabang terlebih dahulu sebelum buka register.");
                    return;
                  }
                  setOpenRegisterModal(true);
                }
              }}
            />
          </div>
        </div>

        {/* ===== Product Scroll Area ===== */}
        <div
          ref={mainScrollRef}
          className={
            "flex-1 overflow-y-auto " +
            (!registerReady ? "opacity-40 pointer-events-none" : "")
          }
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-5 md:px-6 pt-4 pb-24">

            {loading && (
              <div className="text-gray-500 mb-3">Loading products…</div>
            )}

            {err && (
              <div className="text-red-600 mb-3">{err}</div>
            )}

            {!loading && !err && filteredProducts.length === 0 && (
              <div className="text-gray-500 mb-3">
                {isHqAdminUser && selectedStoreId === "ALL"
                  ? "Tidak ada produk (semua cabang)."
                  : "Tidak ada produk di cabang ini."}
              </div>
            )}

            <ProductGrid
              products={filteredProducts}
              onAddToCart={handleProductClick}
            />

            {loadingMore && (
              <div className="text-center py-3 text-gray-500">
                Loading more…
              </div>
            )}

            {!hasMore && filteredProducts.length > 0 && (
              <div className="text-center py-3 text-gray-400 text-sm">
                Semua produk sudah ditampilkan
              </div>
            )}

          </div>
        </div>

      </main>

      {/* Desktop order panel */}
      <aside
        className={
          "hidden md:block order-2 w-full md:w-[340px] md:w-[400px] xl:w-[480px] " +
          "bg-white border-t md:border-t-0 md:border-l border-gray-200 " +
          "p-4 sm:p-5 md:p-6 overflow-y-auto overflow-x-hidden md:sticky md:top-0 md:h-screen min-w-0 " +
          "relative z-10 " +
          (!registerReady ? "opacity-40 pointer-events-none" : "")
        }
      >
        <OrderDetails
          items={cartItems}
          itemDiscounts={itemDiscounts}
          onUpdateQuantity={handleUpdateQuantity}
          onUpdateDiscount={handleUpdateDiscount}
          onRemoveItem={handleRemoveItem}
        />

        <SaleSubmitter
          items={cartItems}
          subtotal={subtotalItems}
          tax={tax}
          additionalCharges={additionalCharges}
          total={total}
          globalDiscounts={globalDiscounts}
          checkout={cartCheckout}
          onCheckoutChange={setCartCheckout}
          onSuccess={(res) => {
            handleClearCart();
            toast.success(
              `Transaction success! Code: ${res?.code || res?.id || "-"}`
            );
          }}
          onCancel={handleClearCart}
          showSummary={true}
          registerOpen={!!currentRegister}
          extraPayload={saleExtraPayload}
        />
      </aside>

      {/* Mobile mini bar */}
      <div className="md:hidden fixed left-0 right-0 bottom-0 z-40 bg-white border-t border-gray-200">
        <button
          onClick={() => setSheetOpen(true)}
          className="w-full h-14 px-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-gray-700" />
            <span className="text-sm font-medium">
              Cart ({cartItems.length})
            </span>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Total</div>
            <div className="text-sm font-semibold">
              Rp{total.toLocaleString("id-ID")}
            </div>
          </div>
          <ChevronUp className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {/* Mobile sheet */}
      <MobileOrderSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        items={cartItems}
        itemDiscounts={itemDiscounts}     // ✅ NEW
        globalDiscounts={globalDiscounts}
        onUpdateQuantity={handleUpdateQuantity}
        onUpdateDiscount={handleUpdateDiscount}
        onRemoveItem={handleRemoveItem}
        subtotal={subtotalItems}
        tax={tax}
        total={total}
        additionalCharges={additionalCharges}
        checkout={cartCheckout}
        onCheckoutChange={setCartCheckout}
        registerOpen={!!currentRegister}
        extraPayload={saleExtraPayload}
        onClearCart={handleClearCart}
      />

      {/* Low ingredient / stock reminder */}
      <LowStockReminderModal
        open={lowStockOpen}
        alerts={lowStockAlerts}
        onClose={dismissLowStockReminder}
        storeLabel={
          stores.find((s) => String(s.id) === String(posStoreId))?.name ?? ""
        }
      />

      {/* Item options picker (Sugar Level, Ice Level, dll) */}
      <OptionPickerModal
        open={!!optionPickerProduct}
        product={optionPickerProduct}
        onClose={() => setOptionPickerProduct(null)}
        onConfirm={handleConfirmOptions}
      />

      {/* Open register modal */}
      <OpenRegisterModal
        open={openRegisterModal}
        onClose={() => setOpenRegisterModal(false)}
        onSubmit={(values) => {
          if (posStoreId == null) {
            toast.error("Pilih cabang terlebih dahulu.");
            return;
          }
          openRegisterMutation.mutate({
            ...values,
            store_location_id: posStoreId,
          });
        }}
        loading={openRegisterMutation.isPending}
        storeLabel={
          stores.find((s) => String(s.id) === String(posStoreId))?.name ?? ""
        }
      />

      {/* Register summary modal (preview & after close) */}
      <RegisterSummaryModal
        open={!!registerSummary}
        onClose={() => {
          setRegisterSummary(null);
          setRegisterClosed(false);
        }}
        data={registerSummary}
        isClosed={registerClosed}
        closing={closeRegisterMutation.isPending}
        onCloseRegister={(payload) => {
          const sid =
            registerSummary?.session?.id ??
            registerSummary?.summary?.session_id ??
            null;
          if (!sid || closeRegisterMutation.isPending) return;

          closeRegisterMutation.mutate({
            id: sid,
            payload: payload || {},
          });
        }}
      />
    </div>
  );
}

function SimpleModal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-[2147483000] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-xl w-[min(720px,100%)] max-h-[90vh] overflow-auto">
        {children}
      </div>
    </div>
  );
}
